import Vue from 'vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import ArticleInfoBreadcrumbs from '@root/modules/article/components/info/ArticleInfoBreadcrumbs.vue';
import ArticleInfoRibbon from '@root/modules/article/components/info/ArticleInfoRibbon.vue';
import ArticleInfoCover from '@root/modules/article/components/info/ArticleInfoCover.vue';
import ArticleInfoSummary from '@root/modules/article/components/info/ArticleInfoSummary.vue';
import ArticleInfoLead from '@root/modules/article/components/info/ArticleInfoLead.vue';
import ArticleInfoTtsPlayer from '@root/modules/article/components/info/ArticleInfoTtsPlayer.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import ContentFragment from '@core/components/ContentFragment.vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import rootConfig from '@root/config/root.config';
import dayjs from 'dayjs';

interface Props {
  article: ArticleData;
  showBodyFragments: number;
  adFree: boolean;
  isAdultConfirmed: boolean;
  isMultipage: boolean;
}

interface Data {
  fontSize: number;
}

interface Methods {
  handleSetFontSize: () => void;
}

interface Computed {
  leadPosition: string;
  articleCommentsPath: string;
  categoryUrl: string;
  articleSettings: ArticleData['settings'];
  comments: boolean | number;
  disabledAdsList: string[];
  disableAds: boolean;
  shouldShowUpdatedDate: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    AdFormBanner,
    ArticleInfoBreadcrumbs,
    ArticleInfoCover,
    ArticleInfoSummary,
    ArticleInfoRibbon,
    ArticleInfoLead,
    ArticleInfoTtsPlayer,
    ContentFragment,
    LinkHandler,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    adFree: {
      type: Boolean,
      required: false,
      default: false,
    },
    isAdultConfirmed: {
      type: Boolean,
      required: false,
      default: false,
    },
    isMultipage: {
      type: Boolean,
      required: false,
      default: false,
    },
    showBodyFragments: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      fontSize: 1,
      showTtsPlayer: false,
    };
  },
  computed: {
    categoryUrl() {
      const { locale } = this.$channelConfig('settings');
      return this.$options.filters!.categoryUrl({ category: this.article.primaryCategory, locale });
    },
    leadPosition() {
      const articleSettings = this.$channelConfig('page').article;
      return articleSettings.component.info.leadPosition;
    },
    articleCommentsPath() {
      const { domain, locale } = this.$channelConfig('settings');
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

      return this.$options.filters!.articleCommentsUrl({ article: this.article, locale, domain, fakeDomainEnabled });
    },
    articleSettings() {
      return this.article.settings || {};
    },
    comments() {
      const { disableComments } = this.article.comments;
      const { comments } = this.article.statistics;

      return !disableComments && !rootConfig.disableComments && comments > 0 && comments;
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    shouldShowUpdatedDate() {
      const publishAt = dayjs(this.article.publishAt);
      const updatedAt = dayjs(this.article.updatedAt);
      const diff = updatedAt.diff(publishAt, 'second');

      return diff >= 3600;
    },
  },
  methods: {
    handleSetFontSize() {
      this.fontSize = this.fontSize === 3 ? 1 : this.fontSize + 1;
      this.$emit('set-font-size');
    },
  },
});
